// #############################################################################
// #
// #  Italian translations
// #
// #############################################################################
// This file contains the Italian translations for the common components of the application.
// It is used across different parts of the application to ensure consistency in language and terminology.
// It is also used to provide a localized experience for italian-speaking users.
// It includes translations for common phrases, error messages, and UI elements that are used throughout the
// application.
// This helps in making the application more user-friendly and accessible to italian-speaking audiences.
// It is important to keep this file updated with any new phrases or changes in the application to ensure that
// the translations remain accurate and relevant.
// #############################################################################

export const langIt = {
  home: {
    welcome: 'Benvenuto nel servizio {service}',
    enter: 'Entra'
  },
  account_type: {
    publisher: 'Editore',
    consumer: 'Aggregatore',
    author: 'Autore'
  },
  app: {
    accountNotSubscribedToCurrentService: 'Il tuo account non è abbonato al servizio corrente'
  },
  login: {
    continue: 'Continua',
    login_tab: 'Accedi',
    logout: 'Esci',
    register_tab: 'Registrati',
    loginTitle: 'Accedi al tuo account',
    chooseSignInMethod: 'Scegli come accedere',
    noAccount: 'Non hai un account?',
    registerButton: 'Crea un account',
    cancel: 'Annulla',
    identifier: 'Identificatore',
    identifierDescription: 'Puoi connetterti usando la tua email',
    identifierPlaceholder: 'Inserisci la tua email',
    usernameOrEmail: 'Nome utente o Email',
    useAnotherAccount: 'Usa un altro account',
    username: 'Nome utente',
    password: 'Password',
    email: 'Email',
    loginButton: 'Accedi',
    signingIn: 'Accesso in corso...',
    signInWithEmail: 'Inviami un link monouso',
    signInWithEmailAgain: 'Richiedi un nuovo link monouso per accedere',
    signInWithEmailDescription: 'Usa l\'email del tuo account',
    signingInWithMagicLink: 'Accesso con link magico...',
    signInWithEmailMessageSent: 'Un link monouso è stato inviato alla tua email, segui il link per accedere.',
    signingInWithEmail: 'Invio link monouso...',
    subscribeButton: 'Abbonati a {type}',
    registerAsButton: 'Registrati come {type}',
    showPassword: 'Mostra Password',
    hidePassword: 'Nascondi Password',
    or: 'o',
    subscriptionSuccess: 'Abbonamento riuscito',
    alternativeAccountDescription: 'Il tuo account non è attualmente abbonato al servizio corrente. Puoi abbonarti o passare ai seguenti servizi.',
    alternativeAccountGoTo: 'Vai a {type}',
    sameAccount: 'Sei già connesso con lo stesso account.',
    sessionConflict: {
      title: 'Conflitto di Sessione',
      message: 'È già aperta una sessione per un altro account.',
      currentSession: 'Sessione corrente:',
      magicLinkSession: 'Sessione link magico:',
      question: 'Vuoi rimanere connesso con la sessione corrente o riconnetterti con la sessione del link magico?',
      stayWithCurrent: 'Resta con la sessione corrente',
      reconnectWithMagicLink: 'Riconnetti con il link magico'
    },
    error: {
      invalidCredentials: 'Credenziali non valide',
      loginFailed: 'Accesso fallito',
      apiConnection: 'Errore di connessione API',
      subscribeFailed: 'Abbonamento fallito: {error}',
      signInWithEmailFailed: 'Accesso con email fallito: {error} (controlla la tua email o nome utente).',
      invalidEmailFormat: 'Inserisci un indirizzo email valido'
    },
    alternativeAccount: {
      contentCreators: 'Sei un creatore di contenuti?',
      contentCreatorsLink: 'Accedi qui',
      authors: 'Sei un autore?',
      authorsLink: 'Accedi qui',
      aggregators: 'Sei un aggregatore?',
      aggregatorsLink: 'Accedi qui',
      author: 'Autore',
      consumer: 'Aggregatore',
      publisher: 'Editore'
    }
  },
  registrationType: {
    publisher: {
      introduction: {
        features: {
          feature1: '',
          feature5: ''
        }
      }
    },
    consumer: {
      introduction: {
        features: {
          feature1: '',
          feature5: ''
        }
      }
    },
    author: {
      introduction: {
        features: {
          feature1: '',
          feature5: ''
        }
      }
    }
  },
  registration: {
    title: 'Registrazione Account',
    introduction: {
      title: 'Benvenuto alla Registrazione',
      subtitle: 'Registrati per il Servizio {service}',
      description: 'Stai per registrarti per il servizio {service}. Questa registrazione ti permetterà di accedere a tutte le funzionalità e capacità disponibili per gli account {service}.',
      startRegistration: 'Inizia Registrazione',
      alreadyHaveAccount: 'Hai già un account? Accedi'
    },
    step_introduction: {
      title: 'Introduzione'
    },
    step_account_setup: {
      title: 'Configurazione Account',
      displayName: 'Nome Visualizzato',
      displayNamePlaceholder: 'Inserisci il tuo nome visualizzato',
      username: 'Nome utente',
      usernamePlaceholder: 'Inserisci il tuo nome utente',
      confirmPassword: 'Conferma Password',
      passwordsDoNotMatch: 'Le password non corrispondono',
      password: 'Password',
      email: 'Email',
      emailPlaceholder: 'Inserisci il tuo indirizzo email',
      affiliation: 'Affiliazione',
      affiliationPlaceholder: 'Università, Azienda o Istituzione',
      homePage: 'Pagina Principale / Home Page',
      homePagePlaceholder: 'https://your-website.com',
      submit: 'Invia Profilo',
      submitting: 'Invio in corso...'
    },
    step_account_verification: {
      title: 'Verifica Account',
      tokenLabel: 'Token di Verifica',
      tokenPlaceholder: 'Inserisci token',
      checkEmail: 'Abbiamo inviato un\'email di verifica al tuo indirizzo. Controlla la tua casella di posta e segui le istruzioni per verificare il tuo account.',
      tokenSent: 'Token di verifica inviato a: {email}',
      resendToken: 'Reinvia Token',
      verifyAccount: 'Verifica Account',
      sendingEmail: 'Invio Email...',
      resendEmail: 'Reinvia Email',
      accountVerified: 'Account verificato con successo!',
      redirecting: 'Reindirizzamento al prossimo passaggio...',
      verifying: 'Controlla Attivazione',
      sendingToken: 'Invio token di verifica...',
      invalidToken: 'Token di verifica non valido. Riprova.',
      tooManyAttempts: 'Troppi tentativi falliti. Richiedi un nuovo token.',
      noToken: 'Nessun token di verifica trovato. Richiedi un nuovo token.',
      verificationSuccess: 'Verifica email riuscita!',
      attemptsRemaining: '{count} tentativi rimanenti',
      resendSuccess: 'Nuovo token di verifica inviato alla tua email.',
      networkError: 'Errore di rete. Controlla la tua connessione e riprova.',
      checkingActivation: 'Controllo attivazione account...',
      emailSentTo: 'Email inviata a:',
      activationEmailResent: 'Email di attivazione inviata con successo! Controlla la tua casella di posta.',
      resendFailed: 'Invio email di attivazione fallito. Riprova.',
      missingIdentifierOrRegistrationToken: 'Identificatore o token di registrazione mancante',
      accountDoesNotHaveAuthorPrivileges: 'L\'account non ha privilegi di autore. Contatta il supporto.',
      accountActivationCheckFailed: 'Controllo attivazione account fallito. Riprova.',
      accountNotYetActivated: 'Account non ancora attivato. Controlla la tua email e clicca sul link di attivazione.',
      invalidCredentials: 'Credenziali non valide. Controlla il tuo identificatore e password.',
      failedToVerifyAccountActivation: 'Verifica attivazione account fallita. Riprova.',
      failedToResendActivationEmail: 'Invio email di attivazione fallito. Riprova.',
      accountRegistrationRejected: 'Registrazione account rifiutata. Contatta il supporto.'
    },
    step_registration_complete: {
      title: 'Completa Registrazione',
      registrationSummary: 'Riepilogo Registrazione',
      profileCreated: 'Profilo creato',
      emailsSent: 'Email inviate agli editori',
      continueToDashboard: 'Continua alla Dashboard',
      continueToLogin: 'Continua al Login'
    }
  },
  common: {
    cancel: 'Annulla',
    save: 'Salva'
  },
  navbar: {
    docs: 'Documentazione',
    account: 'Account',
    settings: 'Impostazioni',
    logout: 'Esci',
    login: 'Accedi',
    pendingRegistrationDesc: 'Completa la tua registrazione per accedere a questo servizio.',
    apps: {
      title: 'App',
      publisher: 'Editore',
      publisherDesc: 'Passa al servizio editore',
      author: 'Autore',
      authorDesc: 'Passa al servizio autore',
      consumer: 'Aggregatore',
      consumerDesc: 'Passa al servizio aggregatore',
      website: 'Sito Web',
      websiteDesc: 'Passa al sito web',
      portal: 'Portale',
      portalDesc: 'Passa al portale',
      currentApplicationDesc: 'Servizio corrente',
      unavailableServiceDesc: 'Servizio non disponibile'
    }
  },
  sidebar: {
    expand: 'Espandi',
    collapse: 'Comprimi',
    info: 'Info',
    infoDesc: 'Informazioni sul servizio',
    showDescriptions: 'Mostra descrizioni',
    hideDescriptions: 'Nascondi descrizioni'
  },
  pagination: {
    previous: 'Precedente',
    next: 'Successivo',
    first: 'Primo',
    last: 'Ultimo',
    page: 'Pagina {current} di {total}',
    itemsPerPage: 'Elementi per pagina'
  },
  table: {
    loading: 'Caricamento...',
    noData: 'Nessun dato',
    noResults: 'Nessun risultato',
    search: 'Cerca'
  },
  form: {
    submit: 'Invia',
    cancel: 'Annulla',
    save: 'Salva',
    loading: 'Caricamento...'
  },
  modal: {
    close: 'Chiudi',
    confirm: 'Conferma',
    cancel: 'Annulla',
    save: 'Salva'
  },
  alert: {
    success: 'Successo',
    error: 'Errore',
    warning: 'Avviso',
    info: 'Info'
  },
  toast: {
    success: 'Successo',
    error: 'Errore',
    warning: 'Avviso',
    info: 'Info',
    close: 'Chiudi'
  },
  tooltip: {
    success: 'Successo',
    error: 'Errore',
    warning: 'Avviso',
    info: 'Info'
  },
  notification: {
    success: 'Successo',
    error: 'Errore',
    warning: 'Avviso',
    info: 'Info'
  },
  notifications: {
    title: 'Notifiche',
    empty: 'Nessuna notifica',
    loading: 'Caricamento notifiche...',
    pageInfo: 'Pagina {current} di {total}',
    error: 'Errore nel caricamento delle notifiche',
    markAsRead: 'Segna come letto',
    markAsUnread: 'Segna come non letto',
    open: 'Apri',
    markAllAsRead: 'Segna tutte come lette',
    unread: 'Non letto',
    all: 'Tutte',
    noContent: 'Nessun contenuto disponibile',
    close: 'Chiudi',
    reload: 'Ricarica notifiche',
    showMore: 'Mostra di più',
    loadingMore: 'Caricamento altre notifiche...',
    errorLoadingMore: 'Errore nel caricamento di altre notifiche',
    noMoreNotifications: 'Nessun\'altra notifica',
    noNotifications: 'Nessuna notifica',
    noNotificationsFound: 'Nessuna notifica trovata'
  },
  account: {
    title: 'Informazioni Account',
    profile_section: 'Profilo',
    api_keys_section: 'Chiavi API',
    security_section: 'Sicurezza',
    loading: 'Caricamento informazioni account...',
    profile: 'Profilo',
    authorProfile: 'Profilo Autore',
    publisherProfile: 'Profilo Editore',
    aggregatorProfile: 'Profilo Aggregatore',
    id: 'ID',
    name: 'Nome',
    email: 'Email',
    services: 'Servizi',
    memberSince: 'Membro dal',
    lastLogin: 'Ultimo Accesso',
    createNewApiKey: 'Crea Nuova Chiave API',
    apiKeysTitle: 'Chiavi API',
    apiKeys: {
      name: 'Nome',
      keyId: 'ID Chiave',
      status: 'Stato',
      lastUsed: 'Ultimo Utilizzo',
      created: 'Creato',
      expires: 'Scade',
      neverUsed: 'Mai Utilizzato',
      noKeys: 'Nessuna chiave API trovata.',
      advancedControls: 'Per controlli avanzati, visita',
      advancedControlsLink: 'la pagina di gestione account'
    },
    security: {
      changePassword: 'Cambia password',
      changePasswordDescription: 'Aggiorna la password del tuo account. Devi inserire la password attuale per confermare la modifica.',
      currentPassword: 'Password attuale',
      currentPasswordPlaceholder: 'Inserisci la password attuale',
      newPassword: 'Nuova password',
      newPasswordPlaceholder: 'Inserisci la nuova password',
      confirmPassword: 'Conferma nuova password',
      confirmPasswordPlaceholder: 'Reinserisci la nuova password',
      changePasswordButton: 'Cambia password',
      changingPassword: 'Modifica in corso...',
      passwordChangedSuccess: 'La password è stata modificata con successo.',
      passwordChangeFailed: 'Modifica password fallita. Verifica la password attuale e riprova.',
      resetPassword: 'Reimposta password',
      resetPasswordDescription: 'Password dimenticata? Possiamo inviare un link di reimpostazione al tuo indirizzo email registrato. Clicca sul link nell\'email per impostare una nuova password.',
      sendResetEmailButton: 'Invia email di reimpostazione',
      sendingResetEmail: 'Invio in corso...',
      resetEmailSent: 'È stata inviata un\'email di reimpostazione password al tuo indirizzo registrato. Controlla la posta in arrivo e segui il link per impostare una nuova password.',
      resetPasswordFailed: 'Invio email di reimpostazione fallito. Riprova.',
      changePrimaryEmail: 'Cambia email principale',
      changePrimaryEmailDescription: 'Aggiorna il tuo indirizzo email principale. Verrà inviata un\'email di verifica al nuovo indirizzo. La nuova email sarà attivata solo dopo la verifica.',
      newEmail: 'Nuova email',
      newEmailPlaceholder: 'Inserisci il nuovo indirizzo email',
      confirmEmail: 'Conferma nuova email',
      confirmEmailPlaceholder: 'Reinserisci il nuovo indirizzo email',
      emailsDoNotMatch: 'Gli indirizzi email non corrispondono',
      changeEmailButton: 'Invia email di verifica',
      sendingVerification: 'Invio in corso...',
      emailVerificationSent: 'È stata inviata un\'email di verifica al tuo nuovo indirizzo. Controlla la posta in arrivo e segui le istruzioni per attivare la nuova email.',
      emailChangeFailed: 'Richiesta di modifica email fallita. Riprova.'
    }
  },
  profileSetup: {
    displayName: 'Nome Visualizzato',
    displayNamePlaceholder: 'Inserisci il tuo nome visualizzato',
    displayNameHelp: 'Il tuo nome visualizzato sarà utilizzato per identificarti nel sistema (questo non è il tuo nome utente).',
    title: 'Configurazione Profilo',
    description: 'Fornisci le tue informazioni professionali per completare il tuo profilo.',
    affiliation: 'Affiliazione',
    affiliationPlaceholder: 'es. Università di Cambridge, MIT, Google Research',
    areaOfInterest: 'Area di Interesse',
    areaOfInterestPlaceholder: 'es. Machine Learning, Computer Vision, NLP',
    homePage: 'Homepage',
    homePagePlaceholder: 'https://your-website.com',
    secondaryEmails: 'Email Secondarie',
    addSecondaryEmail: 'Aggiungi Email Secondaria',
    secondaryEmailPlaceholder: 'il tuo indirizzo email',
    secondaryEmailsHelp: 'Le tue email secondarie',
    save: 'Salva Profilo',
    saving: 'Salvataggio...',
    nextPage: 'Prossima Pagina',
    update: 'Aggiorna Profilo',
    updating: 'Aggiornamento...',
    avatarUrl: 'URL Avatar',
    avatarUrlPlaceholder: 'https://example.com/avatar.jpg',
    changeAvatar: 'Cambia Avatar',
    noAvatar: 'Nessun avatar',
    success: {
      saved: 'Profilo salvato con successo!'
    },
    errors: {
      affiliationRequired: 'L\'affiliazione è obbligatoria.',
      areaOfInterestRequired: 'L\'area di interesse è obbligatoria.',
      invalidHomePage: 'Inserisci un URL valido per la tua homepage.',
      tooManySecondaryEmails: 'Puoi aggiungere un massimo di 5 email secondarie.',
      invalidSecondaryEmail: 'Inserisci un indirizzo email valido.',
      saveFailed: 'Salvataggio profilo fallito. Riprova.',
      invalidAvatarUrl: 'Inserisci un URL avatar valido.'
    }
  },
  profile: {
    key_DISPLAY_NAME: 'Nome Visualizzato',
    key_NAME: 'Nome',
    key_EMAIL: 'Email',
    key_AFFILIATION: 'Affiliazione',
    key_AREA_OF_INTEREST: 'Area di Interesse',
    key_HOME_PAGE: 'Homepage',
    key_EMAILS: 'Email',
    key_AVATAR_URL: 'URL Avatar',
    key_ORCID: 'ORCID',
    key_LINKEDIN: 'LinkedIn',
    key_GOOGLE_SCHOLAR: 'Google Scholar'
  },
  profileSetupView: {
    title: 'Configurazione Profilo',
    description: 'Configura il tuo profilo per accedere a questo servizio.',
    update: 'Aggiorna Profilo',
    editProfile: 'Modifica Profilo'
  },
  bankInfoForm: {
    title: 'Aggiungi/Aggiorna Informazioni Bancarie',
    accountHolder: 'Nome Intestatario:',
    iban: 'IBAN:',
    bic: 'Codice BIC/SWIFT:',
    save: 'Salva Informazioni Bancarie',
    saved: 'Informazioni bancarie salvate localmente (per questa demo).'
  },
  settings: {
    title: 'Impostazioni',
    theme: 'Tema',
    light: 'Chiaro',
    dark: 'Scuro',
    layout: 'Layout',
    helpEnabled: 'Mostra messaggi di aiuto',
    sidebar: 'Barra laterale',
    navbar: 'Barra di navigazione',
    save: 'Salva',
    saved: 'Impostazioni salvate!',
    language: 'Lingua',
    experimentations: 'Sperimentazioni',
    enabled: 'Abilitato',
    disabled: 'Disabilitato'
  },
  apiKeyModal: {
    title: 'Crea Nuova Chiave API',
    namePlaceholder: 'Nome per la chiave API',
    nameHelp: 'Inserisci un nome per la chiave API, questo ti aiuterà a identificare la chiave in seguito.',
    createButton: 'Crea Chiave API',
    yourKeyIs: 'La tua nuova chiave API è:',
    copyWarning: 'Copia e salva questa chiave. Non potrai più vederla!',
    copy: 'Copia',
    copied: 'Copiato!',
    close: 'Chiudi',
    error: 'Creazione chiave API fallita.'
  },
  lang: {
    en: 'EN',
    fr: 'FR',
    de: 'DE',
    es: 'ES'
  },
  theme: {
    switchToLight: 'Passa alla modalità chiara',
    switchToDark: 'Passa alla modalità scura'
  }
};
